import { Component, render, type JSXChild, type JSXProps } from 'engine/index';
import { type JSONObject, lateinit } from 'shared/util';
import type { z, ZodSchema } from 'zod';
import { Button } from './Button';
import { parseForm, renderPath } from 'shared/proptree';
import type { FileType } from 'shared/modelTypes';
import { Icon } from './Icon';
import { File } from 'lucide-static';
import type { FileUpload } from 'shared/modelTypes/common';
import { rpc } from 'src/backend';

/* eslint-disable @typescript-eslint/no-explicit-any */
type FormFieldProps<T, U = JSXProps> = {
	name: string;
	label: string | HTMLElement;
	value?: T;
	optional?: boolean;
	readonly?: boolean;
	validator?: (value?: T) => string[];
} & JSXProps &
	U;

export abstract class FormField<T, U = JSXProps> extends Component<FormFieldProps<T, U>> {
	errorView: HTMLElement = lateinit;
	field: HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement = lateinit;

	override render(): HTMLElement {
		const r = this.renderField();

		this.errorView = r.errorView;

		// @ts-expect-error lazy coersion
		this.field = r.field;
		if (this.props.readonly) {
			this.field.setAttribute('readonly', '');
			this.field.setAttribute('disabled', '');
		}

		//@ts-expect-error custom event
		r.view.addEventListener(
			'focus-field',
			(e: FocusFieldEvent) => {
				if (e.showError) {
					this.runValidator();
					setTimeout(() => {
						this.view.scrollIntoView({
							behavior: 'smooth',
							block: 'center',
						});
						this.field.focus();
						if ('select' in this.field) {
							this.field.select();
						}
					}, 0);
				}
			},
			true,
		);

		return r.view;
	}

	abstract renderField(): {
		view: HTMLElement;
		errorView: HTMLElement;
		field: HTMLElement;
	};

	abstract getValue(): T | undefined;
	abstract setValue(value: T | undefined): void;

	runValidator() {
		const inputValue = this.getValue();

		if (this.props.validator) {
			const errors = this.props.validator(inputValue);
			if (!errors.length) {
				if (this.view.hasAttribute('data-invalid')) {
					this.view.removeAttribute('data-invalid');
				}
			} else {
				const errorMessage = errors[0];
				// console.log('msg', errorMessage);

				this.errorView.textContent = errorMessage.startsWith('validation.') ? __([errorMessage]) : errorMessage;
				this.view.setAttribute('data-invalid', '');
			}
		}
	}
}

const textFieldClasses =
	'block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-theme-600 focus-visible:bg-gray-50 focus-visible:outline-none group-data-[invalid]:bg-danger-50 group-data-[invalid]:ring-danger-500 dark:bg-gray-900 dark:text-gray-100 dark:ring-gray-700 dark:focus:ring-theme-700/80 dark:focus-visible:bg-gray-950 sm:text-sm sm:leading-6';

export class TextField extends FormField<
	string,
	{
		label: string | HTMLElement;
		placeholder?: string;
		compact?: boolean;
		type?: string;
		size?: number;
		labelWidth?: string;
	}
> {
	input: HTMLInputElement = lateinit;

	override renderField(): {
		view: HTMLElement;
		errorView: HTMLElement;
		field: HTMLElement;
	} {
		const p = this.props;

		const view: HTMLElement = (
			<FieldContainer compact={p.compact} name={p.name}>
				<FieldLabel label={p.label} labelWidth={p.labelWidth} name={p.name} />
				<div class="relative mt-1 min-w-0 flex-1">
					<input
						id={p.name + '-field'}
						name={p.name}
						type={p.type ?? 'text'}
						autocomplete="off"
						required={!p.optional ? true : undefined}
						value={p.value ?? ''}
						placeholder={p.placeholder}
						class={textFieldClasses}
						size={p.size ? p.size.toString() : undefined}
						on:input={this.runValidator.bind(this)}
					/>
					<FieldValidationContainer />
				</div>
			</FieldContainer>
		);

		const errorView = view.querySelector('[validation-error]') as HTMLElement;
		this.input = view.querySelector('input') as HTMLInputElement;

		return { view, errorView, field: this.input };
	}

	override getValue(): string | undefined {
		return this.input.value ? this.input.value : undefined;
	}

	override setValue(value: string | undefined) {
		this.input.value = value ?? '';
	}
}

export class PasswordField extends FormField<
	string,
	{
		label: string | HTMLElement;
		placeholder?: string;
		compact?: boolean;
		type?: string;
		labelWidth?: string;
		strengthIndicator?: boolean;
	}
> {
	input: HTMLInputElement = lateinit;

	override renderField(): {
		view: HTMLElement;
		errorView: HTMLElement;
		field: HTMLElement;
	} {
		const p = this.props;

		const view: HTMLElement = (
			<FieldContainer compact={p.compact} name={p.name}>
				<FieldLabel label={p.label} labelWidth={p.labelWidth} name={p.name} />
				<div class="relative mt-1">
					<input
						id={p.name + '-field'}
						name={p.name}
						type="password"
						autocomplete="current-password"
						required={!p.optional ? true : undefined}
						value={p.value ?? ''}
						placeholder={p.placeholder}
						class={textFieldClasses}
						on:input={(e: InputEvent) => {
							this.runValidator();
							if (p.strengthIndicator) {
								const s = this.ratePasswordStrength((e.target as HTMLInputElement)!.value);
								strengthIndicator.style.setProperty('--strength', `${s * 100}%`);
								if (s < 0.4) {
									strengthIndicator.setAttribute('data-rating', 'weak');
								} else if (s < 0.65) {
									strengthIndicator.setAttribute('data-rating', 'ok');
								} else {
									strengthIndicator.setAttribute('data-rating', 'good');
								}
							}
						}}
					/>
					{p.strengthIndicator && (
						<div class="mt-1 flex items-center gap-2">
							<span class="text-sm text-gray-700">Strength:</span>

							<div class="h-2 w-full overflow-hidden rounded-full bg-gray-100">
								<div
									strength-indicator
									class="h-full transition-all ease-linear data-[rating=good]:bg-success-600 data-[rating=ok]:bg-warn-600 data-[rating=weak]:bg-danger-600"
									style="width: var(--strength);"
								></div>
							</div>
						</div>
					)}
					<FieldValidationContainer />
				</div>
			</FieldContainer>
		);

		const errorView = view.querySelector('[validation-error]') as HTMLElement;
		const strengthIndicator = view.querySelector('[strength-indicator]') as HTMLElement;

		this.input = view.querySelector('input') as HTMLInputElement;

		return { view, errorView, field: this.input };
	}

	ratePasswordStrength(password: string): number {
		let score = 0;

		// Base score factors
		const lengthScore = Math.min(password.length / 12, 1);
		const hasLower = /[a-z]/.test(password) ? 0.2 : 0;
		const hasUpper = /[A-Z]/.test(password) ? 0.2 : 0;
		const hasNumber = /[0-9]/.test(password) ? 0.3 : 0;
		const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password) ? 0.3 : 0;

		const lengthMultiplier = 0.5 + 0.5 * Math.min(password.length / 12, 1);

		const varietyScore = (hasLower + hasUpper + hasNumber + hasSpecial) * lengthMultiplier;

		score = lengthScore * 0.6 + varietyScore * 0.4;

		if (!hasLower) score *= 0.85;
		if (!hasUpper) score *= 0.85;
		if (!hasNumber) score *= 0.85;
		if (!hasSpecial) score *= 0.85;

		return Math.min(score, 1); // Cap the score at 1
	}

	override getValue(): string | undefined {
		return this.input.value ? this.input.value : undefined;
	}

	override setValue(value: string | undefined) {
		this.input.value = value ?? '';
	}
}

export class NumberField extends FormField<number, { min?: number; max?: number; step?: number; value: number }> {
	input: HTMLInputElement = lateinit;

	override renderField(): {
		view: HTMLElement;
		errorView: HTMLElement;
		field: HTMLElement;
	} {
		const p = this.props;

		const view: HTMLElement = (
			<FieldContainer compact={p.compact} name={p.name}>
				<FieldLabel label={p.label} labelWidth={p.labelWidth} name={p.name} />
				<div class="relative mt-1">
					<input
						id={p.name + '-field'}
						name={p.name}
						type="number"
						autocomplete="off"
						required={!p.optional ? true : undefined}
						value={p.value ?? ''}
						placeholder={p.placeholder}
						class={textFieldClasses}
						min={p.min ?? 0}
						max={p.max ?? 1_000_000}
						step={p.step ?? 1}
						on:input={this.runValidator.bind(this)}
					/>
					<FieldValidationContainer />
				</div>
			</FieldContainer>
		);

		const errorView = view.querySelector('[validation-error]') as HTMLElement;
		this.input = view.querySelector('input') as HTMLInputElement;

		return { view, errorView, field: this.input };
	}

	override getValue(): number | undefined {
		const v = this.input.valueAsNumber;
		return isNaN(v) ? undefined : v;
	}

	override setValue(value: number | undefined): void {
		if (value === undefined) {
			this.input.value = '';
		} else {
			this.input.valueAsNumber = value;
		}
	}
}

export class FileField extends FormField<
	string | null,
	{
		label: string | HTMLElement;
		compact?: boolean;
		labelWidth?: string;
		file?: FileType;
	}
> {
	input: HTMLInputElement = lateinit;
	image: HTMLImageElement = lateinit;
	preview: HTMLElement = lateinit;

	override renderField(): { view: HTMLElement; errorView: HTMLElement; field: HTMLElement } {
		const p = this.props;

		const view: HTMLElement = (
			<FieldContainer compact={p.compact} name={p.name}>
				<FieldLabel label={p.label} labelWidth={p.labelWidth} name={p.name} />
				<div class="relative mt-1 flex flex-1 flex-col items-start gap-5">
					<div preview></div>
					<input
						id={p.name + '-field'}
						name={p.name}
						type="file"
						// class={textFieldClasses}
						class={'absolute size-0 opacity-0'}
						on:change={this.handleFileChange.bind(this)}
						data-value={JSON.stringify(p.file)}
					/>
					<FieldValidationContainer />
				</div>
			</FieldContainer>
		);

		this.preview = view.querySelector('[preview]') as HTMLElement;
		this.renderPreview();

		const errorView = view.querySelector('[validation-error]') as HTMLElement;
		this.input = view.querySelector('input') as HTMLInputElement;

		return { view, errorView, field: this.input };
	}

	renderPreview(imgUrl: string | undefined = undefined) {
		const p = this.props;
		const hasFile = p.file && p.file.uuid && p.file.extension;

		const previewContent = (
			<label
				for={p.name + '-field'}
				class="group relative block size-32 cursor-pointer overflow-hidden rounded-md border border-dashed border-gray-300"
				data-file={imgUrl || hasFile ? true : undefined}
			>
				{(imgUrl || hasFile) && (
					<img
						class="size-full object-contain group-data-[file]:group-hover:blur-sm"
						src={imgUrl ?? `/uploads/${p.file!.uuid}.${p.file!.extension}`}
					/>
				)}
				<div class="absolute inset-0 grid place-items-center content-center gap-1 transition-opacity duration-100 group-data-[file]:bg-white/70 group-data-[file]:opacity-0 group-data-[file]:hover:opacity-100">
					<Icon icon={File} class="size-6 text-gray-500" />
					<small class="text-gray-800">Upload File</small>
				</div>
			</label>
		);

		render(this.preview, previewContent);
	}

	handleFileChange(event: Event) {
		const file = (event.target as HTMLInputElement)?.files?.[0];
		if (!file) return;

		this.renderPreview(URL.createObjectURL(file));
	}

	override getValue(): string | undefined {
		return this.input && this.input.files && this.input.files[0] && this.input.files[0].name ? this.input.files[0].name : undefined;
	}

	override setValue(_value: string | undefined) {
		// unable to set a file
	}
}

export class DropdownField<T = string> extends FormField<
	T,
	{
		label: string | HTMLElement;
		options: Array<{ value: T; label: string }>;
		placeholder?: string;
		compact?: boolean;
		labelWidth?: string;
		onChange?: (val: T | undefined) => void;
	}
> {
	select: HTMLSelectElement = lateinit;

	override renderField(): {
		view: HTMLElement;
		errorView: HTMLElement;
		field: HTMLElement;
	} {
		const p = this.props;
		const view: HTMLElement = (
			<FieldContainer compact={p.compact} name={p.name}>
				<FieldLabel label={p.label} labelWidth={p.labelWidth} name={p.name} />
				<div class="relative mt-1">
					<select
						id={p.name + '-field'}
						name={p.name}
						required={!p.optional ? true : undefined}
						class={textFieldClasses}
						on:change={(e: Event) => {
							this.runValidator();
							if (this.props.onChange) {
								this.props.onChange(this.getValue()!);
							}
						}}
					>
						{p.placeholder && (
							<option value="" disabled selected={!p.value}>
								{p.placeholder}
							</option>
						)}
						{p.options.map((option) => (
							<option value={String(option.value)} selected={option.value === p.value}>
								{option.label}
							</option>
						))}
					</select>
					<FieldValidationContainer />
				</div>
			</FieldContainer>
		);

		const errorView = view.querySelector('[validation-error]') as HTMLElement;
		this.select = view.querySelector('select') as HTMLSelectElement;
		queueMicrotask(() => {
			if (this.props.onChange) {
				this.props.onChange(this.getValue()!);
			}
		});
		return { view, errorView, field: this.select };
	}

	override getValue(): T | undefined {
		if (!this.select.value) return undefined;

		// Find the original typed value from options
		const selectedOption = this.props.options.find((opt) => String(opt.value) === this.select.value);
		const v = selectedOption?.value;
		return v;
	}

	override setValue(value: T | undefined) {
		this.select.value = value !== undefined ? String(value) : '';
	}
}

export class ToggleField extends FormField<boolean, { onChange?: (val: boolean | undefined) => void }> {
	input: HTMLInputElement = lateinit;

	override renderField(): {
		view: HTMLElement;
		errorView: HTMLElement;
		field: HTMLElement;
	} {
		const p = this.props;

		const view: HTMLElement = (
			<FieldContainer compact={p.compact} name={p.name}>
				<FieldLabel label={p.label} labelWidth={p.labelWidth} name={p.name} />
				<div class="relative mt-1">
					<label class="toggle">
						<input
							id={p.name + 'field'}
							name={p.name}
							type="checkbox"
							checked={!!p.value || undefined}
							class=""
							on:change={(e: Event) => {
								if (this.props.onChange) {
									this.props.onChange(this.getValue()!);
								}
							}}
						/>
						<span class="toggle-view"></span>
					</label>
					<FieldValidationContainer />
				</div>
			</FieldContainer>
		);

		const errorView = view.querySelector('[validation-error]') as HTMLElement;
		this.input = view.querySelector('input') as HTMLInputElement;

		return { view, errorView, field: this.input };
	}

	override getValue(): boolean | undefined {
		return this.input.checked;
	}

	override setValue(value: boolean | undefined): void {
		this.input.checked = value ?? false;
	}
}

export class CheckboxField extends FormField<boolean, { onChange?: (val: boolean | undefined) => void }> {
	input: HTMLInputElement = lateinit;

	override renderField(): {
		view: HTMLElement;
		errorView: HTMLElement;
		field: HTMLElement;
	} {
		const p = this.props;

		const view: HTMLElement = (
			<FieldContainer compact={p.compact} name={p.name}>
				<FieldLabel label={p.label} labelWidth={p.labelWidth} name={p.name} />
				<input
					id={p.name + 'field'}
					name={p.name}
					type="checkbox"
					checked={!!p.value || undefined}
					class=""
					on:change={(e: Event) => {
						if (this.props.onChange) {
							this.props.onChange(this.getValue()!);
						}
					}}
				/>
				<FieldValidationContainer />
			</FieldContainer>
		);

		const errorView = view.querySelector('[validation-error]') as HTMLElement;
		this.input = view.querySelector('input') as HTMLInputElement;

		return { view, errorView, field: this.input };
	}

	override getValue(): boolean | undefined {
		return this.input.checked;
	}

	override setValue(value: boolean | undefined): void {
		this.input.checked = value ?? false;
	}
}

export function FieldContainer({ compact, name }: { compact?: boolean; name: string }, children: JSXChild[]) {
	return (
		<div form-field={name} class={'group ' + (!compact ? undefined : 'flex flex-col gap-2 md:flex-row md:items-center')}>
			{children}
		</div>
	);
}

export function FieldLabel({ label, labelWidth, name }: { name: string; label: HTMLElement | string; labelWidth?: string }) {
	const labelIsElement = label instanceof HTMLElement;

	if (labelIsElement) return label;

	return (
		<label
			for={name + '-field'}
			class="block text-sm font-medium leading-6 text-gray-900 group-data-[invalid]:text-danger-900 dark:text-gray-100"
			style={labelWidth ? `width: ${labelWidth}` : ''}
		>
			{label}
		</label>
	);
}

export function FieldValidationContainer() {
	return <div validation-error class="mt-1 hidden p-1 text-xs leading-none text-danger-700 group-data-[invalid]:block"></div>;
}

/**
 * use zod schema as a form validation error provider
 */
export function validator(zs: ZodSchema) {
	return function (val: any) {
		const r = zs.safeParse(val);
		if (!r.error) return [];
		// console.log('error res', r.error);
		return r.error.errors.map((e) => e.message ?? e.code ?? 'invalid');
	};
}

type FormProps<S extends ZodSchema | undefined = undefined> = {
	onSubmit: (data: S extends ZodSchema ? z.infer<S> : JSONObject) => void;
	schema?: S;
} & JSXProps;

export function Form<S extends ZodSchema | undefined = undefined>({ onSubmit, schema, ...props }: FormProps<S>, children: JSXChild[]) {
	const view: HTMLFormElement = (
		<form on:submit={handleSubmit} class="flex flex-col gap-6" {...props} novalidate>
			{children}
		</form>
	);

	const errorsArea = view.querySelector('[form-validation-errors-area]') as HTMLElement;

	async function handleSubmit(e: SubmitEvent) {
		e.preventDefault();

		const form = view;

		const fileToBase64 = (file: File): Promise<string> =>
			new Promise((resolve, reject) => {
				const reader = new FileReader();
				reader.onload = () => {
					const result = reader.result as string;
					resolve(result.split(',')[1]); // Remove Data URL prefix
				};
				reader.onerror = reject;
				reader.readAsDataURL(file);
			});

		// TODO: set this function through some config system
		// These general purpose form components shouldn't implement app specific logic
		const fileHandler = async (element: HTMLInputElement) => {
			// TODO: support multi-file uploads
			const file = element.files ? element.files[0] : null;

			let value: FileType | undefined = undefined;
			if (file) {
				const dotIndex = file.name.lastIndexOf('.');
				const upload: FileUpload = {
					name: file.name,
					data: await fileToBase64(file),
					size: file.size,
					type: file.type,
					extension: dotIndex === -1 || dotIndex === 0 ? '' : file.name.slice(dotIndex + 1),
				};
				value = await rpc.uploadFile.query(upload);
			} else {
				// Do we have an old stored value?
				const oldValue = element.getAttribute('data-value');
				if (oldValue) {
					value = JSON.parse(oldValue);
				}
			}

			return value;
		};
		const rawData = await parseForm(form, fileHandler);

		if (schema === undefined) {
			onSubmit(rawData as any);
		} else {
			const res = schema.safeParse(rawData);
			if (res.error) {
				// first we try to focus a field with an error
				for (const err of res.error.errors) {
					const path = renderPath(err.path);
					const inputField = view.querySelector(`[form-field="${path}"]`) as HTMLElement;
					if (inputField) {
						let p: HTMLElement = inputField;
						// emit focus event to the field and all its containers
						// components like tabs and dropdowns may want to change themselves to make the field visible
						while (p) {
							const e = new FocusFieldEvent(inputField as HTMLElement, true);
							p.dispatchEvent(e);
							if (p === view) break;
							p = p.parentElement as HTMLElement;
						}
						return;
					}
				}

				// FALLBACK: remaining errors we not coming from a specific field. show them below the submit button
				const errorsToDisplay = res.error.errors.map((e) => (
					<li>
						{renderPath(e.path)}:{e.message ?? e.code ?? 'invalid'}
					</li>
				));

				// res.error.console.error('TODO: handle error', res.error);
				errorsArea.innerHTML = '';
				errorsArea.append(<ul>{errorsToDisplay}</ul>);
			} else {
				onSubmit(res.data);
			}
		}
	}

	return view;
}

type ArrayFieldProps<T> = {
	label: string;
	labelSingle: string;
	fixedSize?: number;
	value: (T | undefined)[];
	horizontal?: boolean;
	entryBuilder: (data: T | undefined, index: number) => HTMLElement;
};
export class ArrayField<T> extends Base.Component<ArrayFieldProps<T>> {
	entryList: HTMLElement = lateinit;
	len: number;
	constructor(props: ArrayFieldProps<T>, children: JSXChild[]) {
		super(props, children);
		this.len = props.value.length;
	}

	override render(): HTMLElement {
		const view: HTMLElement = (
			<div class={'group flex flex-col gap-2'}>
				<span class="flex items-center gap-5 text-sm font-medium leading-6 text-gray-900 group-data-[invalid]:text-danger-900 dark:text-gray-100">
					{this.props.label}
					{!this.props.fixedSize && (
						<Button type="button" mode="secondary" size="md" on:click={() => this.addNewEntry()}>
							Add
						</Button>
					)}
				</span>
				<div entry-list class={this.props.horizontal ? 'flex w-full flex-row gap-8 overflow-auto' : 'flex flex-col gap-4'}></div>
			</div>
		);
		this.entryList = view.querySelector('[entry-list]') as HTMLElement;

		if (this.props.fixedSize !== undefined) {
			const size = this.props.fixedSize;
			const vals = this.props.value;
			vals.length = size;
			for (let i = 0; i < size; i++) {
				if (vals[i] === undefined) vals[i] = undefined;
			}
		}

		this.renderEntries();

		return view;
	}

	addNewEntry() {
		const i = this.len;
		const entry = this.props.entryBuilder(undefined, i);
		this.entryList.append(entry);
		this.len++;
	}

	renderEntries() {
		const entries = this.props.value.map((v, i) => {
			const r = this.props.entryBuilder(v, i);
			r.style.flexShrink = '0';
			return r;
		});
		this.entryList.innerHTML = '';
		this.entryList.append(...entries);
	}
}

export function SubmitButton(props: JSXProps, children: JSXChild[]) {
	return (
		<div submit-btn-area>
			<Button submit-btn {...props}>
				{children}
			</Button>
			<div form-validation-errors-area class="text-danger-800"></div>
		</div>
	);
}

export class FocusFieldEvent extends Event {
	constructor(
		public field: HTMLElement,
		public showError: boolean = false,
	) {
		super('focus-field');
	}
}
